import { Children } from 'react'
import { cn } from './internal/utils'
import { BORDER, RADIUS } from './internal/presets'

const Item = ({ children }) => children

/** antd's `column` accepts a number or a responsive object; take the widest match. */
const resolveColumn = (column) => {
  if (typeof column === 'number') return column
  if (typeof column === 'object' && column !== null) {
    return column.lg ?? column.md ?? column.sm ?? column.xs ?? 3
  }
  return 3
}

/**
 * antd Descriptions. Accepts the `items` array (v5) or `Descriptions.Item`
 * children, and renders `bordered` / `layout="vertical"` variants.
 */
const Descriptions = ({
  title,
  extra,
  items,
  column = 3,
  bordered = false,
  layout = 'horizontal',
  size = 'default',
  colon = true,
  labelStyle,
  contentStyle,
  className,
  style,
  children,
  ...rest
}) => {
  const resolved =
    items?.length
      ? items
      : Children.toArray(children)
          .filter(Boolean)
          .map((child, index) => ({
            key: child.key ?? index,
            label: child.props?.label,
            span: child.props?.span,
            children: child.props?.children,
          }))

  const columns = resolveColumn(column)
  const isVertical = layout === 'vertical'
  const padding = size === 'small' ? 'px-2 py-1.5' : 'px-3 py-2'

  return (
    <div className={cn('ui-descriptions', className)} style={style} {...rest}>
      {title || extra ? (
        <div className="ui-descriptions-header mb-2 flex items-center justify-between gap-3">
          <div className="ui-descriptions-title font-semibold text-[var(--text-dark)]">{title}</div>
          {extra ? <div className="ui-descriptions-extra">{extra}</div> : null}
        </div>
      ) : null}

      <div
        className={cn(
          'ui-descriptions-view grid',
          bordered && `overflow-hidden border ${BORDER} ${RADIUS}`
        )}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {resolved.map((item, index) => (
          <div
            key={item.key ?? index}
            className={cn(
              'ui-descriptions-item flex min-w-0',
              isVertical ? 'flex-col' : 'flex-row',
              bordered && `border-b border-r ${BORDER}`
            )}
            style={{ gridColumn: item.span ? `span ${Math.min(item.span, columns)}` : undefined }}
          >
            <div
              className={cn(
                'ui-descriptions-item-label shrink-0 text-sm text-[var(--text-muted)]',
                padding,
                bordered && 'bg-[var(--gray-100)]',
                !isVertical && 'w-2/5'
              )}
              style={labelStyle}
            >
              {item.label}
              {colon && !isVertical && item.label ? ' :' : null}
            </div>
            <div
              className={cn(
                'ui-descriptions-item-content min-w-0 flex-1 break-words text-sm text-[var(--text-dark)]',
                padding
              )}
              style={contentStyle}
            >
              {item.children ?? item.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

Descriptions.Item = Item

export default Descriptions
